import Session from "@lib/session";
import router from "next/router";
import loginProfile from "@services/login_profile";
import logoutProfile from "@services/logout_profile";
import { getContent } from "@lib/use_content";
interface UpdateFromUrlResponse {
  redirectTo?: string;
  error?: string;
}

// Manage configuration coming down from the CMS Plugin via URL params.
export default class FrameConfig implements be.FrameConfig {
  bookingUrl?: string;
  groupCode?: string;
  jsonHeaders?: { string: string };
  language?: string = "en";
  mode?: string; // ex. clientcenter
  override?: string = "";
  lockGroupProfile?: boolean;
  parentUrl?: string;
  resetPasswordUrl?: string;
  serviceArea?: number;
  serviceAreaName?: string;
  tokenGroup?: string = "BookingEngine";

  constructor() {
    if (!global.sessionStorage) return;

    let config = sessionStorage.getItem(`${router.query.siteId}.frameConfig`);
    let configObj: be.FrameConfig;

    if (config === null) return;

    try {
      configObj = JSON.parse(config);
    } catch (e) {
      return;
    }

    if (configObj) {
      this.groupCode = configObj.groupCode;
      if (configObj.language) this.language = configObj.language;
      window.language = this.language || "en";
      if (configObj.override) this.override = configObj.override;
      if (configObj.jsonHeaders) this.jsonHeaders = configObj.jsonHeaders;

      this.bookingUrl = configObj.bookingUrl;
      this.lockGroupProfile = configObj.lockGroupProfile;
      this.mode = configObj.mode;
      this.parentUrl = configObj.parentUrl;
      this.resetPasswordUrl = configObj.resetPasswordUrl;
      this.serviceArea = configObj.serviceArea;
      this.serviceAreaName = configObj.serviceAreaName;
      if (configObj.tokenGroup) this.tokenGroup = configObj.tokenGroup;
    }
  }

  // Apply frame config to the booking data. Generally called after clearing the session.
  apply() {
    this.applyServiceArea();
  }

  async applyServiceArea() {
    Session.updateConfig({
      serviceArea: this.serviceArea,
    });
  }

  async clearCodes() {
    await logoutProfile("GROUP");
    await logoutProfile("SALES");
    await Session.clearSession();
    await global.window.apolloClient.resetStore();
  }

  save() {
    if (!global.sessionStorage) return null;
    sessionStorage.setItem(`${router.query.siteId}.frameConfig`, JSON.stringify(this));
  }

  async setGroupCode(groupCode: string) {
    if (groupCode == this.groupCode) return;

    // Clean up group code.
    groupCode = groupCode.replace(/^[^a-z\d]*|[^a-z\d]*$/gi, "");

    await Session.clearSession();
    const { actualType, error, success } = await loginProfile({ id: groupCode, type: "GROUP" });

    if (success) {
      this.groupCode = groupCode;
      this.save();
      await global.window.apolloClient.resetStore();
      return { error: false, success: true, profileType: actualType };
    } else {
      this.groupCode = undefined;
      this.save();
      await global.window.apolloClient.resetStore();
      return { error };
    }
  }

  async setMode(mode: string) {
    if (mode == this.mode) return;
    this.mode = mode;
    this.save();
    await Session.clearSession();
    await global.window.apolloClient.resetStore();
  }

  async setServiceArea(serviceArea: number | undefined) {
    if (serviceArea == this.serviceArea) return;
    const { data: content, error: cError } = await getContent(["serviceAreaName"], serviceArea);

    Session.clearReservationData();
    this.serviceArea = serviceArea;
    this.serviceAreaName = content?.content?.serviceAreaName;
    await this.applyServiceArea();
    this.save();
  }

  update(name: keyof be.FrameConfig, value: any) {
    this[name] = value;
    this.save();
  }

  async updateFromURL(): Promise<UpdateFromUrlResponse | undefined> {
    let action = undefined;

    // Booking URL
    if (router.query["attached_config[main_frame_page]"]) {
      this.bookingUrl = router.query["attached_config[main_frame_page]"].toString();
      // Fix weird bug- double backslashes are getting replaced with single backslashes somewhere.
      this.bookingUrl = this.bookingUrl.replace(/https?:\/(\w)/, "https://$1");
      this.save();
    }

    // Current parent URL
    if (router.query["ac[current_parent_url]"]) {
      this.parentUrl = router.query["ac[current_parent_url]"].toString();
      this.save();
    }

    // Reset password URL
    if (router.query["ac[reset_password_url]"]) {
      this.resetPasswordUrl = router.query["ac[reset_password_url]"].toString();
      // Fix weird bug- double backslashes are getting replaced with single backslashes somewhere.
      this.resetPasswordUrl = this.resetPasswordUrl.replace(/https?:\/(\w)/, "https://$1");
      this.save();
    }

    // Language
    const currentLang = this.language;
    const language = router.query["locale"] || router.query["lang"] || this.language || "en";
    this.language = language.toString().toLowerCase();
    window.language = this.language;
    this.save();
    if (currentLang != this.language) await global.window.apolloClient.resetStore();

    // Overrides
    const currentOverride = this.override;
    if (router.query.hasOwnProperty("or")) this.override = router.query["or"];
    else this.override = this.override || "";
    this.save();
    if (currentOverride != this.override) await global.window.apolloClient.resetStore();

    // JSON Headers
    const headerObj = {};
    Object.entries(router.query).forEach(([key, value]) => {
      if (key.startsWith("header_")) {
        let headerKey = key.replace("header_", "");
        headerObj[headerKey] = value;
      }
    });
    if (Object.keys(headerObj).length != 0) this.jsonHeaders = headerObj;
    else this.jsonHeaders = this.jsonHeaders || {};
    this.save();

    // Application mode (ex. clientcenter)
    if (router.query["mode"]) this.setMode(router.query["mode"].toString());

    // Service area
    if (router.query["ac[service_area]"] || router.query["trip[service_area]"]) {
      let serviceArea = router.query["ac[service_area]"] || router.query["trip[service_area]"];
      await this.setServiceArea(parseInt(serviceArea as string));
    }

    // Lock group profile
    if (router.query["ac[lock_group_profile]"]) {
      this.lockGroupProfile = !!Number(router.query["ac[lock_group_profile]"]);
      this.save();
    }

    // Group code
    if (router.query["code"]) {
      let returnVal = await this.setGroupCode(router.query["code"] as string);
      if (returnVal?.error == "PASSWORD_NEEDED") {
        action = { redirectTo: `group_password?code=${router.query.code}` };
      } else if (returnVal?.error) {
        action = { error: returnVal?.error };
      }
    }

    // Detach codes
    if (router.query.detach_all_codes) {
      await this.clearCodes();
    }

    return action;
  }
}
